import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useUser } from "../../user/utils/user";

import { useRoleClientSelection } from "../contexts/RoleClientSelectionContext";
import { resolveRoute } from "../utils/routeHelpers";

function ClientSelection({ t }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const { selectedRole, setSelectedClient } = useRoleClientSelection();

  const roleClients = user?.user?.role_clients || [];
  const clientsForRole = roleClients.filter(
    (rc) => rc.role_name === selectedRole,
  );

  useEffect(() => {
    if (!selectedRole) {
      navigate("/roles");
    }
  }, [selectedRole, navigate]);

  useEffect(() => {
    if (clientsForRole.length === 1 && selectedRole) {
      const clientName = clientsForRole[0].client_name;
      setSelectedClient(clientName);
      const targetPath = resolveRoute("/task", {
        role: selectedRole,
        client: clientName,
      });
      navigate(targetPath);
    }
  }, [clientsForRole, selectedRole, setSelectedClient, navigate]);

  const handleClientSelect = (clientName) => {
    setSelectedClient(clientName);
    const targetPath = resolveRoute("/task", {
      role: selectedRole,
      client: clientName,
    });
    navigate(targetPath);
  };

  const handleBack = () => {
    navigate("/roles");
  };

  if (!selectedRole) {
    return null;
  }

  if (clientsForRole.length === 0) {
    return (
      <div className="text-center">
        <h1 className="text-xl font-semibold text-gray-900">
          {t("clientSelection.noClients", "No clients available")}
        </h1>
        <p className="mt-4 text-gray-600">
          {t(
            "clientSelection.noClientsForRole",
            "No clients are available for this role.",
          )}
        </p>
        <button
          onClick={handleBack}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-700 hover:bg-cyan-600"
        >
          {t("clientSelection.back", "Back to Role Selection")}
        </button>
      </div>
    );
  }

  if (clientsForRole.length === 1) {
    return (
      <div className="text-center">
        <div className="animate-pulse">
          <p className="text-gray-600">
            {t("clientSelection.redirecting", "Redirecting...")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <button
        onClick={handleBack}
        aria-label="Back"
        className="mb-4 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
      >
        ← {t("clientSelection.back", "Back")}
      </button>
      <h1 className="text-xl font-semibold text-gray-900">
        {t("clientSelection.selectClient", "Select Client for ")}
        {selectedRole}{" "}
      </h1>
      <p className="mt-2 text-gray-600">
        {t(
          "clientSelection.chooseClient",
          "Choose the client you want to work with.",
        )}
      </p>
      <div className="mt-6 flex flex-col items-center space-y-4 max-w-md mx-auto">
        {clientsForRole.map((rc) => (
          <button
            key={rc.client_name}
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 w-full"
            onClick={() => handleClientSelect(rc.client_name)}
          >
            {rc.client_name.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}

export default withTranslation()(ClientSelection);
